// src/pages/CosteoPage.js
import React, { useState, useEffect, useCallback } from 'react';
import { getProducts } from '../api/apiService';

const bs  = { fontFamily: "'Montserrat', sans-serif" };
const ds  = { fontFamily: "'Cormorant Garamond', Georgia, serif" };
const API = '/api';

const fmt    = (n) => Number(n || 0).toFixed(2);
const fmtUSD = (n) => `USD $${Number(n || 0).toFixed(2)}`;
const pct    = (n) => (Number(n || 0) * 100).toFixed(1) + '%';

// ── Estilos constantes a nivel de módulo ──────────────────────────────────
const INP = {
  ...bs, padding: '9px 12px', border: '1px solid #e5e0e0', fontSize: '12px',
  color: '#060D14', outline: 'none', borderRadius: '2px', background: 'white',
  transition: 'border-color 0.2s', width: '100%', boxSizing: 'border-box',
};
const LBL = {
  ...bs, display: 'block', fontSize: '10px', letterSpacing: '0.15em',
  textTransform: 'uppercase', color: '#999', marginBottom: '5px',
};
const focusRojo = (e) => { e.target.style.borderColor = '#961914'; };
const blurGris  = (e) => { e.target.style.borderColor = '#e5e0e0'; };

// ── Cálculo por ítem — TODO EN USD, conversión al final ──────────────────
const calcItem = (item, orden) => {
  const tc          = parseFloat(orden.precio_dolar) || 1;
  const totalPiezas = orden.cantidad_total || 1;

  // Costo China en USD = precio_usd × cantidad
  const costoChina_usd = (parseFloat(item.precio_usd) || 0) * item.cantidad;

  // Gastos proporcionales en USD
  const envio_usd  = (parseFloat(orden.envio_total)       / totalPiezas) * item.cantidad;
  const tax_usd    = (parseFloat(orden.tax_total)         / totalPiezas) * item.cantidad;
  const import_usd = (parseFloat(orden.importacion_total) / totalPiezas) * item.cantidad;

  // Subtotal en USD
  const subtotal_usd    = costoChina_usd + envio_usd + tax_usd + import_usd;

  // Costo unitario en USD y MXN
  const costoUnit_usd   = item.cantidad > 0 ? subtotal_usd / item.cantidad : 0;
  const costoUnit_mxn   = costoUnit_usd * tc;

  // Subtotal total en MXN
  const subtotal_mxn    = subtotal_usd * tc;

  // Venta en MXN
  const ventaTotal_mxn  = (parseFloat(item.precio_venta) || 0) * item.cantidad;
  const utilidad_mxn    = ventaTotal_mxn - subtotal_mxn;
  const margen          = ventaTotal_mxn > 0 ? utilidad_mxn / ventaTotal_mxn : 0;

  return {
    costoChina_usd, envio_usd, tax_usd, import_usd,
    subtotal_usd, costoUnit_usd, costoUnit_mxn,
    subtotal_mxn, ventaTotal_mxn, utilidad_mxn, margen,
  };
};

// ── Componentes de UI a nivel de módulo ───────────────────────────────────
const Card = ({ children, style }) => (
  <div style={{ background: 'white', border: '1px solid #f0eaea',
    borderRadius: '2px', padding: '20px', ...style }}>
    {children}
  </div>
);

const SecLabel = ({ children }) => (
  <p style={{ ...bs, fontSize: '10px', fontWeight: 700, letterSpacing: '0.2em',
    textTransform: 'uppercase', color: '#961914', margin: '0 0 16px' }}>
    {children}
  </p>
);

const KPI = ({ label, value, color = '#060D14', small }) => (
  <div style={{ background: '#faf9f9', border: '1px solid #f0eaea',
    borderRadius: '2px', padding: '12px 16px' }}>
    <p style={{ ...bs, fontSize: '9px', color: '#bbb', letterSpacing: '0.15em',
      textTransform: 'uppercase', margin: '0 0 4px' }}>{label}</p>
    <p style={{ ...ds, fontSize: small ? '1rem' : '1.3rem', color,
      fontWeight: 400, margin: 0 }}>{value}</p>
  </div>
);

// ── Selector de producto desde BD ─────────────────────────────────────────
// onSelect recibe el objeto completo { name, price } para autocompletar precio
const ProductoSelector = ({ value, onSelect }) => {
  const [productos, setProductos] = useState([]);
  const [open,      setOpen]      = useState(false);
  const [search,    setSearch]    = useState('');

  useEffect(() => { getProducts().then(setProductos).catch(() => {}); }, []);

  const filtrados = productos.filter(p =>
    p.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div style={{ position: 'relative' }}>
      <div style={{ display: 'flex', gap: '6px' }}>
        <input style={{ ...INP, flex: 1 }} value={value}
          onChange={e => onSelect({ name: e.target.value, price: null })}
          onFocus={focusRojo} onBlur={blurGris}
          placeholder="Nombre del producto" />
        <button type="button" onClick={() => setOpen(o => !o)}
          style={{ ...bs, background: open ? '#961914' : 'white',
            color: open ? 'white' : '#961914', border: '1px solid #e5c9c9',
            padding: '0 10px', cursor: 'pointer', borderRadius: '2px',
            fontSize: '10px', fontWeight: 600, whiteSpace: 'nowrap',
            transition: 'all 0.2s' }}>
          {open ? '✕' : '▼ BD'}
        </button>
      </div>
      {open && (
        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0,
          zIndex: 50, background: 'white', border: '1px solid #f0eaea',
          borderRadius: '2px', boxShadow: '0 8px 24px rgba(0,0,0,0.1)',
          maxHeight: '220px', overflow: 'hidden', display: 'flex',
          flexDirection: 'column' }}>
          <div style={{ padding: '8px' }}>
            <input autoFocus style={{ ...INP, padding: '7px 10px' }}
              placeholder="Buscar producto…" value={search}
              onChange={e => setSearch(e.target.value)}
              onFocus={focusRojo} onBlur={blurGris} />
          </div>
          <div style={{ overflowY: 'auto', flex: 1 }}>
            {filtrados.length === 0
              ? <p style={{ ...bs, fontSize: '12px', color: '#bbb', padding: '12px' }}>
                  Sin resultados
                </p>
              : filtrados.map(p => (
                <button key={p.id} type="button"
                  onMouseDown={() => {
                    onSelect({ name: p.name, price: Number(p.price) });
                    setOpen(false);
                    setSearch('');
                  }}
                  style={{ ...bs, display: 'block', width: '100%', textAlign: 'left',
                    background: 'none', border: 'none', cursor: 'pointer',
                    padding: '9px 12px', fontSize: '12px', color: '#060D14',
                    borderBottom: '1px solid #faf5f5', transition: 'background 0.15s' }}
                  onMouseOver={e => e.currentTarget.style.background = '#fdf5f5'}
                  onMouseOut={e => e.currentTarget.style.background = 'none'}>
                  <span style={{ fontWeight: 600 }}>{p.name}</span>
                  {p.price && (
                    <span style={{ color: '#961914', marginLeft: '8px', fontSize: '10px',
                      fontWeight: 600 }}>${Number(p.price).toFixed(2)}</span>
                  )}
                  {p.categoryName && (
                    <span style={{ color: '#bbb', marginLeft: '6px', fontSize: '10px' }}>
                      {p.categoryName}
                    </span>
                  )}
                </button>
              ))
            }
          </div>
        </div>
      )}
    </div>
  );
};

// ── Estado inicial ─────────────────────────────────────────────────────────
const ORDEN_EMPTY = {
  nombre: '', fecha_orden: new Date().toISOString().split('T')[0],
  precio_dolar: '', cantidad_total: '',
  envio_total: '', tax_total: '', importacion_total: '', notas: '',
  importacion_auto: true,
};
const ITEM_EMPTY = { nombre_producto: '', cantidad: '', precio_usd: '', precio_venta: '' };

// ── Formulario ─────────────────────────────────────────────────────────────
const FormularioCosteo = ({ initial, onSaved, onCancel }) => {
  const [orden,  setOrden]  = useState(initial?.orden  || ORDEN_EMPTY);
  const [items,  setItems]  = useState(initial?.items  || [{ ...ITEM_EMPTY }]);
  const [saving, setSaving] = useState(false);

  const setO = useCallback((k, v) => setOrden(p => ({ ...p, [k]: v })), []);

  // Envío: monto total que pagaste de envío en MXN
  // Tax: tax total en MXN
  // Importación automática: 16% del costo China total de todos los ítems
  const tc          = parseFloat(orden.precio_dolar) || 1;
  const totalPiezas = parseInt(orden.cantidad_total) || 1;

  // Total China en USD = suma de precio_usd × cantidad de cada item
  const costoChina_usd_total = items.reduce((s, it) =>
    s + (parseFloat(it.precio_usd) || 0) * (parseInt(it.cantidad) || 0), 0);

  const envio_usd  = parseFloat(orden.envio_total)        || 0;
  const tax_usd    = parseFloat(orden.tax_total)          || 0;
  const import_usd = parseFloat(orden.importacion_total)  || 0;

  const gastosExtra_usd = envio_usd + tax_usd + import_usd;
  const totalOrden_usd  = costoChina_usd_total + gastosExtra_usd;
  const totalOrden_mxn  = totalOrden_usd * tc;

  const totalVenta_mxn  = items.reduce((s, it) =>
    s + (parseFloat(it.precio_venta) || 0) * (parseInt(it.cantidad) || 0), 0);
  const totalUtilidad   = totalVenta_mxn - totalOrden_mxn;
  const totalMargen     = totalVenta_mxn > 0 ? totalUtilidad / totalVenta_mxn : 0;

  const ordenNum = {
    precio_dolar:      tc,
    cantidad_total:    totalPiezas,
    envio_total:       envio_usd,
    tax_total:         tax_usd,
    importacion_total: import_usd,
  };

  const handleCambioTC = useCallback((v) => setO('precio_dolar', v), [setO]);
  const handleEnvio    = useCallback((v) => setO('envio_total', v), [setO]);
  const handleTax      = useCallback((v) => setO('tax_total', v), [setO]);

  const toggleImportacionAuto = useCallback(() => {
    setOrden(p => ({
      ...p,
      importacion_auto:  !p.importacion_auto,
      importacion_total: !p.importacion_auto
        ? (costoChina_usd_total * 0.16).toFixed(4)
        : p.importacion_total,
    }));
  }, [costoChina_usd_total]);

  // Recalcular importación automática cuando cambian los items
  useEffect(() => {
    if (orden.importacion_auto) {
      setOrden(p => ({
        ...p,
        importacion_total: (costoChina_usd_total * 0.16).toFixed(4),
      }));
    }
  }, [costoChina_usd_total, orden.importacion_auto]);

  const addItem    = useCallback(() => setItems(p => [...p, { ...ITEM_EMPTY }]), []);
  const removeItem = useCallback((i) => setItems(p => p.filter((_, idx) => idx !== i)), []);
  const setItem    = useCallback((i, k, v) => setItems(p => {
    const n = [...p]; n[i] = { ...n[i], [k]: v }; return n;
  }), []);

  const handleSave = async () => {
    if (!orden.nombre || !orden.fecha_orden || !totalPiezas) {
      alert('Completa nombre, fecha y cantidad total.'); return;
    }
    const itemsValidos = items.filter(it =>
      it.nombre_producto.trim() && parseInt(it.cantidad) > 0
    );
    if (!itemsValidos.length) { alert('Agrega al menos un producto.'); return; }

    setSaving(true);
    try {
      // Guardamos costo_productos como el total China en MXN para compatibilidad con la BD
      const body = {
        ...(initial?.id ? { id: initial.id } : {}),
        ...orden,
        precio_dolar:      tc,
        cantidad_total:    totalPiezas,
        costo_productos:   costoChina_usd_total * tc, // total China en MXN para BD
        envio_total:       envio_usd,
        tax_total:         tax_usd,
        importacion_total: import_usd,
        items: itemsValidos.map(it => ({
          nombre_producto: it.nombre_producto.trim(),
          cantidad:        parseInt(it.cantidad),
          precio_usd:      parseFloat(it.precio_usd) || 0,
          precio_venta:    parseFloat(it.precio_venta) || 0,
        })),
      };
      const res  = await fetch(`${API}/save_costeo.php`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        credentials: 'include', body: JSON.stringify(body),
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.message);
      onSaved();
    } catch (e) { alert('Error: ' + e.message); }
    finally { setSaving(false); }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

      {/* ── Datos de la orden ──────────────────────────────────────────── */}
      <Card>
        <SecLabel>Datos de la Orden</SecLabel>
        <div style={{ display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill,minmax(180px,1fr))', gap: '12px' }}>

          <div style={{ gridColumn: '1/-1' }}>
            <label style={LBL}>Nombre de la orden *</label>
            <input style={INP} value={orden.nombre}
              onChange={e => setO('nombre', e.target.value)}
              onFocus={focusRojo} onBlur={blurGris}
              placeholder="Ej: Alibaba Enero 2025 — Zapatillas" />
          </div>

          <div>
            <label style={LBL}>Fecha de orden *</label>
            <input type="date" style={INP} value={orden.fecha_orden}
              onChange={e => setO('fecha_orden', e.target.value)}
              onFocus={focusRojo} onBlur={blurGris} />
          </div>

          <div>
            <label style={LBL}>Tipo de cambio USD → MXN *</label>
            <div style={{ position: 'relative' }}>
              <input type="number" step="0.0001" style={INP}
                value={orden.precio_dolar}
                onChange={e => handleCambioTC(e.target.value)}
                onFocus={focusRojo} onBlur={blurGris}
                placeholder="Ej: 20.85" />
            </div>
            {tc > 1 && (
              <p style={{ ...bs, fontSize: '10px', color: '#bbb', margin: '3px 0 0' }}>
                USD $1.00 = MXN ${fmt(tc)}
              </p>
            )}
          </div>

          <div>
            <label style={LBL}>Total de piezas en la orden *</label>
            <input type="number" min="1" style={INP}
              value={orden.cantidad_total}
              onChange={e => setO('cantidad_total', e.target.value)}
              onFocus={focusRojo} onBlur={blurGris}
              placeholder="Ej: 117" />
          </div>
        </div>

        {/* Gastos adicionales en MXN */}
        <div style={{ height: '1px', background: '#f5f0f0', margin: '16px 0' }} />
        <p style={{ ...bs, fontSize: '10px', fontWeight: 700, letterSpacing: '0.18em',
          textTransform: 'uppercase', color: '#bbb', marginBottom: '12px' }}>
          Gastos Adicionales (USD)
        </p>

        <div style={{ display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill,minmax(180px,1fr))', gap: '12px' }}>

          <div>
            <label style={LBL}>Envío total (USD)</label>
            <input type="number" step="0.0001" style={INP}
              value={orden.envio_total}
              onChange={e => handleEnvio(e.target.value)}
              onFocus={focusRojo} onBlur={blurGris}
              placeholder="$0.00" />
          </div>

          <div>
            <label style={LBL}>Tax total (USD)</label>
            <input type="number" step="0.0001" style={INP}
              value={orden.tax_total}
              onChange={e => handleTax(e.target.value)}
              onFocus={focusRojo} onBlur={blurGris}
              placeholder="$0.00" />
          </div>

          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between',
              alignItems: 'center', marginBottom: '5px' }}>
              <label style={{ ...LBL, marginBottom: 0 }}>Importación / Aduana (USD)</label>
              <button type="button" onClick={toggleImportacionAuto} style={{
                ...bs, background: 'none', border: 'none', cursor: 'pointer',
                fontSize: '9px', fontWeight: 700, letterSpacing: '0.1em',
                textTransform: 'uppercase', padding: '2px 8px', borderRadius: '20px',
                color: orden.importacion_auto ? 'white' : '#961914',
                background: orden.importacion_auto ? '#961914' : 'none',
                border: `1px solid ${orden.importacion_auto ? '#961914' : '#e5c9c9'}`,
                transition: 'all 0.2s',
              }}>
                {orden.importacion_auto ? 'Auto 16%' : 'Manual'}
              </button>
            </div>
            <input type="number" step="0.01" style={{
              ...INP,
              background: orden.importacion_auto ? '#fdf9f9' : 'white',
              color: orden.importacion_auto ? '#961914' : '#060D14',
            }}
              value={orden.importacion_total}
              onChange={e => setO('importacion_total', e.target.value)}
              disabled={orden.importacion_auto}
              onFocus={focusRojo} onBlur={blurGris}
              placeholder="$0.00" />
            {orden.importacion_auto && (
              <p style={{ ...bs, fontSize: '10px', color: '#bbb', margin: '3px 0 0',
                fontStyle: 'italic' }}>
                16% del costo China total
              </p>
            )}
          </div>
        </div>

        {/* Resumen costos */}
        {(costoChina_usd_total > 0 || gastosExtra_usd > 0) && (
          <div style={{ marginTop: '14px', background: '#faf9f9',
            border: '1px solid #f0eaea', borderRadius: '2px',
            padding: '12px 16px', display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
            <div>
              <p style={{ ...bs, fontSize: '9px', color: '#bbb', letterSpacing: '0.15em',
                textTransform: 'uppercase', margin: '0 0 2px' }}>Costo China (USD)</p>
              <p style={{ ...ds, fontSize: '1.1rem', color: '#3b5bdb', margin: 0 }}>
                USD ${fmt(costoChina_usd_total)}
              </p>
            </div>
            <div>
              <p style={{ ...bs, fontSize: '9px', color: '#bbb', letterSpacing: '0.15em',
                textTransform: 'uppercase', margin: '0 0 2px' }}>Gastos adicionales (USD)</p>
              <p style={{ ...ds, fontSize: '1.1rem', color: '#666', margin: 0 }}>
                USD ${fmt(gastosExtra_usd)}
              </p>
            </div>
            <div>
              <p style={{ ...bs, fontSize: '9px', color: '#bbb', letterSpacing: '0.15em',
                textTransform: 'uppercase', margin: '0 0 2px' }}>Total orden (USD)</p>
              <p style={{ ...ds, fontSize: '1.1rem', color: '#666', margin: 0 }}>
                USD ${fmt(totalOrden_usd)}
              </p>
            </div>
            <div>
              <p style={{ ...bs, fontSize: '9px', color: '#bbb', letterSpacing: '0.15em',
                textTransform: 'uppercase', margin: '0 0 2px' }}>Total orden (MXN)</p>
              <p style={{ ...ds, fontSize: '1.2rem', color: '#060D14', margin: 0, fontWeight: 600 }}>
                ${fmt(totalOrden_mxn)}
              </p>
            </div>
            <div>
              <p style={{ ...bs, fontSize: '9px', color: '#bbb', letterSpacing: '0.15em',
                textTransform: 'uppercase', margin: '0 0 2px' }}>Costo por pieza (prom MXN)</p>
              <p style={{ ...ds, fontSize: '1.2rem', color: '#961914', margin: 0 }}>
                ${fmt(totalPiezas > 0 ? totalOrden_mxn / totalPiezas : 0)}
              </p>
            </div>
          </div>
        )}

        <div style={{ marginTop: '12px' }}>
          <label style={LBL}>Notas</label>
          <textarea style={{ ...INP, resize: 'none', height: '52px' }}
            value={orden.notas} onChange={e => setO('notas', e.target.value)}
            onFocus={focusRojo} onBlur={blurGris}
            placeholder="Observaciones de la orden…" />
        </div>
      </Card>

      {/* ── Productos ──────────────────────────────────────────────────── */}
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between',
          alignItems: 'center', marginBottom: '16px' }}>
          <SecLabel>Productos de la Orden</SecLabel>
          <button type="button" onClick={addItem} style={{
            ...bs, background: '#961914', color: 'white', border: 'none',
            padding: '7px 16px', cursor: 'pointer', fontSize: '10px',
            fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase',
            borderRadius: '2px', transition: 'background 0.2s',
            display: 'flex', alignItems: 'center', gap: '6px',
          }}
          onMouseOver={e => e.currentTarget.style.background = '#7a140f'}
          onMouseOut={e => e.currentTarget.style.background = '#961914'}>
            + Agregar Producto
          </button>
        </div>

        {/* Header columnas */}
        <div style={{ display: 'grid',
          gridTemplateColumns: '1fr 70px 100px 100px 110px 110px 90px 36px',
          gap: '6px', padding: '0 0 8px', borderBottom: '1px solid #f0eaea',
          marginBottom: '8px' }}>
          {['Producto','Cant','USD c/u','MXN China','Costo Unit. MXN','P. Venta MXN','Utilidad',''].map(h => (
            <span key={h} style={{ ...bs, fontSize: '9px', fontWeight: 700,
              letterSpacing: '0.12em', textTransform: 'uppercase', color: '#bbb' }}>{h}</span>
          ))}
        </div>

        {/* Filas */}
        {items.map((item, i) => {
          const it = {
            nombre_producto: item.nombre_producto,
            cantidad:        parseInt(item.cantidad)      || 0,
            precio_usd:      parseFloat(item.precio_usd)  || 0,
            precio_venta:    parseFloat(item.precio_venta) || 0,
          };
          const cal   = calcItem(it, ordenNum);
          const valid = it.cantidad > 0 && it.precio_usd > 0 && tc > 0;

          return (
            <div key={i} style={{ display: 'grid',
              gridTemplateColumns: '1fr 70px 100px 100px 110px 110px 90px 36px',
              gap: '6px', marginBottom: '8px', alignItems: 'center' }}>

              <ProductoSelector
                value={item.nombre_producto}
                onSelect={({ name, price }) => {
                  setItem(i, 'nombre_producto', name);
                  if (price !== null) setItem(i, 'precio_venta', price);
                }} />

              <input type="number" min="1" style={{ ...INP, padding: '7px 8px', textAlign: 'center' }}
                value={item.cantidad}
                onChange={e => setItem(i, 'cantidad', e.target.value)}
                onFocus={focusRojo} onBlur={blurGris}
                placeholder="0" />

              {/* Precio USD de Alibaba */}
              <div style={{ position: 'relative' }}>
                <span style={{ position: 'absolute', left: '8px', top: '50%',
                  transform: 'translateY(-50%)', fontSize: '10px', color: '#bbb',
                  ...bs }}>$</span>
                <input type="number" step="0.0001" min="0"
                  style={{ ...INP, padding: '7px 8px 7px 18px', textAlign: 'right' }}
                  value={item.precio_usd}
                  onChange={e => setItem(i, 'precio_usd', e.target.value)}
                  onFocus={focusRojo} onBlur={blurGris}
                  placeholder="0.00" />
              </div>

              {/* Costo China en MXN — calculado */}
              <div style={{ background: '#faf9f9', border: '1px solid #f0eaea',
                borderRadius: '2px', padding: '7px 8px', textAlign: 'right' }}>
                <span style={{ ...bs, fontSize: '11px', color: '#666' }}>
                  {valid ? `$${fmt(cal.costoChina_usd * ordenNum.precio_dolar)}` : '—'}
                </span>
              </div>

              {/* Costo unitario MXN (China + proporcionales) */}
              <div style={{ background: '#fdf8f8', border: '1px solid #f0eaea',
                borderRadius: '2px', padding: '7px 8px', textAlign: 'right' }}>
                <span style={{ ...bs, fontSize: '12px', color: '#961914', fontWeight: 600 }}>
                  {valid ? `$${fmt(cal.costoUnit_mxn)}` : '—'}
                </span>
              </div>

              {/* Precio de venta */}
              <input type="number" step="0.01" min="0"
                style={{ ...INP, padding: '7px 8px', textAlign: 'right' }}
                value={item.precio_venta}
                onChange={e => setItem(i, 'precio_venta', e.target.value)}
                onFocus={focusRojo} onBlur={blurGris}
                placeholder="$0.00" />

              {/* Utilidad */}
              <div style={{
                background: valid && cal.utilidad_mxn < 0 ? '#fef2f2' : '#f0fdf4',
                border: `1px solid ${valid && cal.utilidad_mxn < 0 ? '#fca5a5' : '#86efac'}`,
                borderRadius: '2px', padding: '7px 8px', textAlign: 'right' }}>
                <span style={{ ...bs, fontSize: '12px', fontWeight: 600,
                  color: valid ? (cal.utilidad_mxn >= 0 ? '#16a34a' : '#dc2626') : '#bbb' }}>
                  {valid ? `$${fmt(cal.utilidad_mxn)}` : '—'}
                </span>
              </div>

              <button type="button" onClick={() => removeItem(i)}
                disabled={items.length === 1}
                style={{ background: 'none', border: 'none',
                  cursor: items.length > 1 ? 'pointer' : 'not-allowed',
                  color: '#ddd', padding: '4px',
                  opacity: items.length === 1 ? 0.3 : 1 }}
                onMouseOver={e => { if (items.length > 1) e.currentTarget.style.color = '#dc2626'; }}
                onMouseOut={e => e.currentTarget.style.color = '#ddd'}>
                <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"/>
                </svg>
              </button>
            </div>
          );
        })}

        {/* Totales globales */}
        {totalPiezas > 0 && (
          <div style={{ marginTop: '16px', paddingTop: '14px',
            borderTop: '2px solid #f0eaea',
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill,minmax(150px,1fr))', gap: '10px' }}>
            {[
              { label: 'Total Venta MXN',     value: `$${fmt(totalVenta_mxn)}`,  color: '#060D14' },
              { label: 'Total Costo USD',      value: `USD $${fmt(totalOrden_usd)}`, color: '#3b5bdb' },
              { label: 'Total Costo MXN',      value: `$${fmt(totalOrden_mxn)}`,  color: '#961914' },
              { label: 'Utilidad Total MXN',   value: `$${fmt(totalUtilidad)}`,   color: totalUtilidad >= 0 ? '#16a34a' : '#dc2626' },
              { label: 'Margen Global',         value: pct(totalMargen),           color: totalMargen >= 0.3 ? '#16a34a' : totalMargen >= 0.15 ? '#d97706' : '#dc2626' },
            ].map(k => <KPI key={k.label} {...k} small />)}
          </div>
        )}
      </Card>

      {/* ── Desglose detallado ─────────────────────────────────────────── */}
      {totalPiezas > 0 && items.some(it => parseInt(it.cantidad) > 0 && parseFloat(it.precio_usd) > 0) && (
        <Card>
          <SecLabel>Desglose Completo por Producto</SecLabel>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #f0eaea' }}>
                  {['Producto','Cant','USD c/u','China USD','Envío USD','Tax USD','Import. USD',
                    'Total USD','Costo Unit. USD','Costo Unit. MXN','P. Venta MXN','Utilidad Unit.','Margen'].map(h => (
                    <th key={h} style={{ ...bs, padding: '6px 8px', fontSize: '9px',
                      fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase',
                      color: '#bbb', textAlign: h === 'Producto' ? 'left' : 'right',
                      whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {items.map((item, i) => {
                  const it = {
                    cantidad:     parseInt(item.cantidad)      || 0,
                    precio_usd:   parseFloat(item.precio_usd)  || 0,
                    precio_venta: parseFloat(item.precio_venta) || 0,
                  };
                  if (!it.cantidad || !it.precio_usd) return null;
                  const cal = calcItem(it, ordenNum);
                  return (
                    <tr key={i} style={{ borderBottom: '1px solid #faf5f5',
                      background: i % 2 === 0 ? 'white' : '#fdfafa' }}>
                      <td style={{ ...bs, padding: '8px', color: '#060D14', fontWeight: 500, whiteSpace: 'nowrap' }}>
                        {item.nombre_producto || `Prod. ${i+1}`}
                        <span style={{ color: '#bbb', fontWeight: 400 }}> ×{it.cantidad}</span>
                      </td>
                      <td style={{ ...bs, padding: '8px', textAlign: 'right', fontSize: '11px', color: '#666' }}>{it.cantidad}</td>
                      <td style={{ ...bs, padding: '8px', textAlign: 'right', fontSize: '11px', color: '#3b5bdb', fontWeight: 600 }}>USD ${fmt(it.precio_usd)}</td>
                      <td style={{ ...bs, padding: '8px', textAlign: 'right', fontSize: '11px', color: '#3b5bdb' }}>USD ${fmt(cal.costoChina_usd)}</td>
                      <td style={{ ...bs, padding: '8px', textAlign: 'right', fontSize: '11px', color: '#666' }}>USD ${fmt(cal.envio_usd)}</td>
                      <td style={{ ...bs, padding: '8px', textAlign: 'right', fontSize: '11px', color: '#666' }}>USD ${fmt(cal.tax_usd)}</td>
                      <td style={{ ...bs, padding: '8px', textAlign: 'right', fontSize: '11px', color: '#666' }}>USD ${fmt(cal.import_usd)}</td>
                      <td style={{ ...bs, padding: '8px', textAlign: 'right', fontSize: '11px', color: '#666', fontWeight: 600 }}>USD ${fmt(cal.subtotal_usd)}</td>
                      <td style={{ ...bs, padding: '8px', textAlign: 'right', fontSize: '11px', color: '#961914', fontWeight: 700 }}>USD ${fmt(cal.costoUnit_usd)}</td>
                      <td style={{ ...bs, padding: '8px', textAlign: 'right', fontSize: '12px', color: '#961914', fontWeight: 700, background: '#fdf8f8' }}>${fmt(cal.costoUnit_mxn)}</td>
                      <td style={{ ...bs, padding: '8px', textAlign: 'right', fontSize: '11px', color: '#060D14' }}>${fmt(it.precio_venta)}</td>
                      <td style={{ ...bs, padding: '8px', textAlign: 'right', fontSize: '12px',
                        color: cal.utilidad_mxn >= 0 ? '#16a34a' : '#dc2626', fontWeight: 600 }}>
                        ${fmt(it.cantidad > 0 ? cal.utilidad_mxn / it.cantidad : 0)}
                      </td>
                      <td style={{ ...bs, padding: '8px', textAlign: 'right', fontSize: '11px', fontWeight: 600,
                        color: cal.margen >= 0.3 ? '#16a34a' : cal.margen >= 0.15 ? '#d97706' : '#dc2626' }}>
                        {pct(cal.margen)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Botones */}
      <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
        <button type="button" onClick={onCancel} style={{
          ...bs, background: 'none', border: '1px solid #e5e0e0', color: '#666',
          padding: '11px 24px', cursor: 'pointer', fontSize: '11px', fontWeight: 600,
          letterSpacing: '0.12em', textTransform: 'uppercase', borderRadius: '2px',
          transition: 'all 0.2s',
        }}
        onMouseOver={e => { e.currentTarget.style.borderColor = '#961914'; e.currentTarget.style.color = '#961914'; }}
        onMouseOut={e => { e.currentTarget.style.borderColor = '#e5e0e0'; e.currentTarget.style.color = '#666'; }}>
          Cancelar
        </button>
        <button type="button" onClick={handleSave} disabled={saving} style={{
          ...bs, background: '#961914', color: 'white', border: 'none',
          padding: '11px 28px', cursor: saving ? 'not-allowed' : 'pointer',
          fontSize: '11px', fontWeight: 600, letterSpacing: '0.15em',
          textTransform: 'uppercase', borderRadius: '2px',
          opacity: saving ? 0.7 : 1, transition: 'background 0.2s',
        }}
        onMouseOver={e => { if (!saving) e.currentTarget.style.background = '#7a140f'; }}
        onMouseOut={e => e.currentTarget.style.background = '#961914'}>
          {saving ? 'Guardando…' : initial?.id ? 'Actualizar Costeo' : 'Guardar Costeo'}
        </button>
      </div>
    </div>
  );
};

// ── Tarjeta historial ─────────────────────────────────────────────────────
const TarjetaCosteo = ({ costeo, onEdit, onDelete, onView }) => {
  const totalCosto = costeo.costo_productos + costeo.envio_total
                   + costeo.tax_total + costeo.importacion_total;
  const utilidad   = costeo.venta_total - totalCosto;
  const margen     = costeo.venta_total > 0 ? utilidad / costeo.venta_total : 0;

  return (
    <div style={{ background: 'white', border: '1px solid #f0eaea',
      borderRadius: '2px', overflow: 'hidden', transition: 'box-shadow 0.2s' }}
    onMouseOver={e => e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.06)'}
    onMouseOut={e => e.currentTarget.style.boxShadow = 'none'}>
      <div style={{ padding: '14px 20px', background: '#fdfafa',
        borderBottom: '1px solid #f5f0f0', display: 'flex',
        justifyContent: 'space-between', alignItems: 'flex-start',
        flexWrap: 'wrap', gap: '8px' }}>
        <div>
          <h3 style={{ ...ds, fontSize: '1.1rem', fontWeight: 600,
            color: '#060D14', margin: '0 0 3px' }}>{costeo.nombre}</h3>
          <p style={{ ...bs, fontSize: '11px', color: '#bbb', margin: 0 }}>
            {new Date(costeo.fecha_orden + 'T12:00:00').toLocaleDateString('es-MX', {
              year: 'numeric', month: 'long', day: 'numeric'
            })} · {costeo.num_productos} producto{costeo.num_productos !== 1 ? 's' : ''}
            {costeo.precio_dolar > 1 && ` · TC: $${fmt(costeo.precio_dolar)}`}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '6px' }}>
          {[{ label: 'Ver', fn: () => onView(costeo.id) },
            { label: 'Editar', fn: () => onEdit(costeo.id) }].map(({ label, fn }) => (
            <button key={label} onClick={fn} style={{
              ...bs, background: 'none', border: '1px solid #e5e0e0', color: '#666',
              padding: '5px 12px', cursor: 'pointer', fontSize: '10px',
              fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase',
              borderRadius: '2px', transition: 'all 0.2s',
            }}
            onMouseOver={e => { e.currentTarget.style.borderColor = '#961914'; e.currentTarget.style.color = '#961914'; }}
            onMouseOut={e => { e.currentTarget.style.borderColor = '#e5e0e0'; e.currentTarget.style.color = '#666'; }}>
              {label}
            </button>
          ))}
          <button onClick={() => onDelete(costeo.id)} style={{
            ...bs, background: 'none', border: '1px solid #fee2e2',
            color: '#dc2626', padding: '5px 8px', cursor: 'pointer',
            fontSize: '10px', borderRadius: '2px', transition: 'all 0.2s',
          }}
          onMouseOver={e => e.currentTarget.style.background = '#fef2f2'}
          onMouseOut={e => e.currentTarget.style.background = 'none'}>
            <svg width="12" height="12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
            </svg>
          </button>
        </div>
      </div>
      <div style={{ padding: '12px 20px', display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
        {[
          { label: 'Costo Orden MXN', val: `$${fmt(totalCosto)}`,        color: '#666' },
          { label: 'Venta Total MXN', val: `$${fmt(costeo.venta_total)}`, color: '#060D14' },
          { label: 'Utilidad',        val: `$${fmt(utilidad)}`,           color: utilidad >= 0 ? '#16a34a' : '#dc2626' },
          { label: 'Margen',          val: pct(margen),                   color: margen >= 0.3 ? '#16a34a' : margen >= 0.15 ? '#d97706' : '#dc2626' },
        ].map(({ label, val, color }) => (
          <div key={label}>
            <p style={{ ...bs, fontSize: '9px', color: '#bbb', letterSpacing: '0.12em',
              textTransform: 'uppercase', margin: '0 0 2px' }}>{label}</p>
            <p style={{ ...bs, fontSize: '13px', fontWeight: 600, color, margin: 0 }}>{val}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

// ── Modal detalle ─────────────────────────────────────────────────────────
const ModalDetalle = ({ costeoId, onClose }) => {
  const [data, setData] = useState(null);

  useEffect(() => {
    fetch(`${API}/get_costeo.php?id=${costeoId}`, { credentials: 'include' })
      .then(r => r.json())
      .then(j => { if (j.success) setData(j.data); })
      .catch(console.error);
  }, [costeoId]);

  if (!data) return null;

  const ordenNum = {
    precio_dolar:      data.precio_dolar,
    cantidad_total:    data.cantidad_total,
    envio_total:       data.envio_total,
    tax_total:         data.tax_total,
    importacion_total: data.importacion_total,
  };

  return (
    <div onClick={e => { if (e.target === e.currentTarget) onClose(); }}
      style={{ position: 'fixed', inset: 0, zIndex: 200,
        background: 'rgba(6,13,20,0.7)', backdropFilter: 'blur(4px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '16px', overflowY: 'auto' }}>
      <div style={{ background: 'white', width: '100%', maxWidth: '860px',
        maxHeight: '90vh', overflowY: 'auto', borderRadius: '2px',
        boxShadow: '0 32px 80px rgba(0,0,0,0.25)' }}>

        <div style={{ padding: '20px 24px', borderBottom: '1px solid #f0eaea',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          position: 'sticky', top: 0, background: 'white', zIndex: 10 }}>
          <div>
            <h2 style={{ ...ds, fontSize: '1.4rem', color: '#060D14', margin: '0 0 2px' }}>
              {data.nombre}
            </h2>
            <p style={{ ...bs, fontSize: '11px', color: '#bbb', margin: 0 }}>
              {new Date(data.fecha_orden + 'T12:00:00').toLocaleDateString('es-MX', {
                year: 'numeric', month: 'long', day: 'numeric'
              })} · TC: ${fmt(data.precio_dolar)} MXN/USD
            </p>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none',
            cursor: 'pointer', color: '#aaa', padding: '4px' }}>
            <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>

        <div style={{ padding: '24px', overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#faf9f9', borderBottom: '1px solid #f0eaea' }}>
                {['Producto','Cant','USD c/u','China USD','Envío USD','Tax USD','Import. USD',
                  'Costo Unit. USD','Costo Unit. MXN','P.Venta MXN','Utilidad Unit.','Margen'].map(h => (
                  <th key={h} style={{ ...bs, padding: '8px', fontSize: '9px',
                    fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase',
                    color: '#bbb', textAlign: h === 'Producto' ? 'left' : 'right',
                    whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.items.map((item, i) => {
                const cal = calcItem(item, ordenNum);
                return (
                  <tr key={i} style={{ borderBottom: '1px solid #faf5f5',
                    background: i % 2 === 0 ? 'white' : '#fdfafa' }}>
                    <td style={{ ...bs, padding: '9px 8px', color: '#060D14', fontWeight: 500 }}>
                      {item.nombre_producto}
                    </td>
                    <td style={{ ...bs, padding: '9px 8px', textAlign: 'right', fontSize: '11px', color: '#666' }}>{item.cantidad}</td>
                    <td style={{ ...bs, padding: '9px 8px', textAlign: 'right', fontSize: '11px', color: '#3b5bdb', fontWeight: 600 }}>
                      USD ${fmt(item.precio_usd || 0)}
                    </td>
                    <td style={{ ...bs, padding: '9px 8px', textAlign: 'right', fontSize: '11px', color: '#3b5bdb' }}>USD ${fmt(cal.costoChina_usd)}</td>
                    <td style={{ ...bs, padding: '9px 8px', textAlign: 'right', fontSize: '11px', color: '#666' }}>USD ${fmt(cal.envio_usd)}</td>
                    <td style={{ ...bs, padding: '9px 8px', textAlign: 'right', fontSize: '11px', color: '#666' }}>USD ${fmt(cal.tax_usd)}</td>
                    <td style={{ ...bs, padding: '9px 8px', textAlign: 'right', fontSize: '11px', color: '#666' }}>USD ${fmt(cal.import_usd)}</td>
                    <td style={{ ...bs, padding: '9px 8px', textAlign: 'right', fontSize: '11px', color: '#961914', fontWeight: 700 }}>USD ${fmt(cal.costoUnit_usd)}</td>
                    <td style={{ ...bs, padding: '9px 8px', textAlign: 'right', fontSize: '12px', color: '#961914', fontWeight: 700, background: '#fdf8f8' }}>${fmt(cal.costoUnit_mxn)}</td>
                    <td style={{ ...bs, padding: '9px 8px', textAlign: 'right', fontSize: '11px', color: '#060D14' }}>${fmt(item.precio_venta)}</td>
                    <td style={{ ...bs, padding: '9px 8px', textAlign: 'right', fontSize: '12px',
                      color: cal.utilidad_mxn >= 0 ? '#16a34a' : '#dc2626', fontWeight: 600 }}>
                      ${fmt(item.cantidad > 0 ? cal.utilidad_mxn / item.cantidad : 0)}
                    </td>
                    <td style={{ ...bs, padding: '9px 8px', textAlign: 'right', fontSize: '11px',
                      fontWeight: 600,
                      color: cal.margen >= 0.3 ? '#16a34a' : cal.margen >= 0.15 ? '#d97706' : '#dc2626' }}>
                      {pct(cal.margen)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {data.notas && (
            <p style={{ ...bs, fontSize: '12px', color: '#888', marginTop: '16px',
              fontStyle: 'italic', borderTop: '1px solid #f0eaea', paddingTop: '12px' }}>
              {data.notas}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Página principal ──────────────────────────────────────────────────────
const CosteoPage = () => {
  const [vista,    setVista]    = useState('lista');
  const [costeos,  setCosteos]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [editData, setEditData] = useState(null);
  const [viewId,   setViewId]   = useState(null);

  const loadCosteos = useCallback(async () => {
    setLoading(true);
    try {
      const res  = await fetch(`${API}/list_costeos.php`, { credentials: 'include' });
      const json = await res.json();
      if (json.success) setCosteos(json.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadCosteos(); }, [loadCosteos]);

  const handleEdit = async (id) => {
    try {
      const res  = await fetch(`${API}/get_costeo.php?id=${id}`, { credentials: 'include' });
      const json = await res.json();
      if (json.success) {
        setEditData({
          id: json.data.id,
          orden: {
            nombre:            json.data.nombre,
            fecha_orden:       json.data.fecha_orden,
            precio_dolar:      json.data.precio_dolar,
            cantidad_total:    json.data.cantidad_total,
            envio_total:       json.data.envio_total,
            tax_total:         json.data.tax_total,
            importacion_total: json.data.importacion_total,
            notas:             json.data.notas || '',
            importacion_auto:  false,
          },
          items: json.data.items.map(it => ({
            nombre_producto: it.nombre_producto,
            cantidad:        it.cantidad,
            precio_usd:      it.precio_usd || 0,
            precio_venta:    it.precio_venta,
          })),
        });
        setVista('editar');
      }
    } catch (e) { console.error(e); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este costeo?')) return;
    try {
      await fetch(`${API}/delete_costeo.php?id=${id}`, { credentials: 'include' });
      await loadCosteos();
    } catch (e) { console.error(e); }
  };

  const handleSaved = () => { setVista('lista'); setEditData(null); loadCosteos(); };

  if (vista === 'nuevo' || vista === 'editar') return (
    <div style={{ padding: '24px 16px', maxWidth: '1200px', margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
        <button onClick={() => { setVista('lista'); setEditData(null); }} style={{
          ...bs, background: 'none', border: '1px solid #e5e0e0', cursor: 'pointer',
          padding: '7px 14px', fontSize: '10px', fontWeight: 600, letterSpacing: '0.12em',
          textTransform: 'uppercase', color: '#666', borderRadius: '2px',
          display: 'flex', alignItems: 'center', gap: '6px',
        }}>
          <svg width="13" height="13" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7"/>
          </svg>
          Volver
        </button>
        <h1 style={{ ...ds, fontSize: '1.8rem', fontWeight: 400, color: '#060D14', margin: 0 }}>
          {vista === 'editar' ? 'Editar Costeo' : 'Nuevo Costeo'}
        </h1>
      </div>
      <FormularioCosteo
        initial={editData}
        onSaved={handleSaved}
        onCancel={() => { setVista('lista'); setEditData(null); }}
      />
    </div>
  );

  return (
    <div style={{ padding: '24px 16px', maxWidth: '1200px', margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between',
        alignItems: 'flex-start', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h1 style={{ ...ds, fontSize: '2rem', fontWeight: 400, color: '#060D14', margin: '0 0 4px' }}>
            Costeo de Órdenes
          </h1>
          <p style={{ ...bs, fontSize: '12px', color: '#bbb', margin: 0 }}>
            {costeos.length} orden{costeos.length !== 1 ? 'es' : ''} registrada{costeos.length !== 1 ? 's' : ''}
          </p>
        </div>
        <button onClick={() => setVista('nuevo')} style={{
          ...bs, background: '#961914', color: 'white', border: 'none',
          padding: '11px 22px', cursor: 'pointer', fontSize: '11px',
          fontWeight: 600, letterSpacing: '0.15em', textTransform: 'uppercase',
          borderRadius: '2px', display: 'flex', alignItems: 'center', gap: '8px',
          transition: 'background 0.2s',
        }}
        onMouseOver={e => e.currentTarget.style.background = '#7a140f'}
        onMouseOut={e => e.currentTarget.style.background = '#961914'}>
          <svg width="13" height="13" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4"/>
          </svg>
          Nueva Orden
        </button>
      </div>

      {loading ? (
        <div style={{ padding: '48px', textAlign: 'center' }}>
          <p style={{ ...bs, fontSize: '12px', color: '#bbb' }}>Cargando órdenes…</p>
        </div>
      ) : costeos.length === 0 ? (
        <div style={{ background: 'white', border: '1px solid #f0eaea',
          borderRadius: '2px', padding: '64px', textAlign: 'center' }}>
          <p style={{ ...ds, fontSize: '1.4rem', color: '#bbb', marginBottom: '8px' }}>
            Aún no hay órdenes registradas
          </p>
          <p style={{ ...bs, fontSize: '12px', color: '#bbb' }}>
            Crea tu primera orden con el botón "Nueva Orden"
          </p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {costeos.map(c => (
            <TarjetaCosteo key={c.id} costeo={c}
              onEdit={handleEdit} onDelete={handleDelete} onView={setViewId} />
          ))}
        </div>
      )}

      {viewId && <ModalDetalle costeoId={viewId} onClose={() => setViewId(null)} />}
    </div>
  );
};

export default CosteoPage;
